import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Truck } from 'lucide-react';

// Service images mapped to each service
const serviceImages = {
  1: "https://images.unsplash.com/photo-1626535683369-0edf8fb3bd58?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Dry Ice Blasting - CO2 vapor
  2: "https://images.unsplash.com/photo-1756561909277-92753f05f032?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Heavy Duty Cleaning - industrial machinery
  3: "https://images.unsplash.com/photo-1645754884968-a00b7ed62c43?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Degreasing - oil/machinery
  4: "https://images.unsplash.com/photo-1771531072574-af6ed6b954c0?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Oil Stain Removal - concrete floor
  5: "https://images.unsplash.com/photo-1768054948628-82319724f0b7?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Equipment Restoration - heavy machinery
  6: "https://images.unsplash.com/photo-1748816964372-8a73dbb4bb7a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Fire & Smoke Damage - fire damaged room
  7: "https://images.unsplash.com/photo-1649777882133-525e923fd5d7?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Mold Remediation - mold on surface
  8: "https://images.unsplash.com/photo-1594573677313-757d4aeea449?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Fleet Cleaning - engine bay
  9: "https://images.unsplash.com/photo-1612082452474-10ce7f932d0b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Paint Stripping - peeling paint metal
  10: "https://images.unsplash.com/photo-1732529877647-822cb6f82fce?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Rust Removal - rusted metal
  11: "https://images.unsplash.com/photo-1757562593192-e15aa89e7876?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // HVAC Cleaning - worker with respirator
  12: "https://images.unsplash.com/photo-1772300704502-410f0fbd43bb?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Concrete Cleaning - warehouse floor
  13: "https://images.unsplash.com/photo-1772209415876-76ea6cbc2f0c?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Facility Maintenance - warehouse workers
  14: "https://images.unsplash.com/photo-1675860094441-5127d5b05820?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Surface Decontamination - industrial facility
  15: "https://images.unsplash.com/photo-1759330806091-b9a077491cc1?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", // Graffiti Removal - wall cleaning
};

const services = [
  { id: 1, name: 'Dry Ice Blasting', slug: 'dry-ice-blasting', desc: 'CO2 cleaning, waterless, non-abrasive. Our signature service uses recycled CO2 pellets accelerated by compressed air to clean surfaces without water, chemicals, or abrasive media.', category: 'core' },
  { id: 2, name: 'Heavy Duty Cleaning', slug: 'heavy-duty-cleaning', desc: 'Industrial-grade decontamination for the toughest jobs. We tackle buildup that traditional methods struggle with.', category: 'core' },
  { id: 3, name: 'Degreasing', slug: 'degreasing', desc: 'Complete removal of grease, oil, and carbon buildup from machinery, equipment, and surfaces.', category: 'core' },
  { id: 4, name: 'Oil Stain Removal', slug: 'oil-stain-removal', desc: 'Effective treatment for concrete, floors, and equipment surfaces with persistent oil contamination.', category: 'core' },
  { id: 5, name: 'Equipment Restoration', slug: 'equipment-restoration', desc: 'Bring aging machinery back to like-new condition, extending equipment life and improving performance.', category: 'core' },
  { id: 6, name: 'Fire & Smoke Damage', slug: 'fire-smoke-damage', desc: 'Professional removal of soot, char, and smoke residue. Critical for post-fire restoration projects.', category: 'specialized' },
  { id: 7, name: 'Mold Remediation', slug: 'mold-remediation', desc: 'Non-toxic mold removal from structures and equipment without introducing moisture or harmful chemicals.', category: 'specialized' },
  { id: 8, name: 'Fleet Cleaning', slug: 'fleet-cleaning', desc: 'Comprehensive cleaning of engine bays, undercarriages, chassis, and frames for fleet vehicles.', category: 'specialized' },
  { id: 9, name: 'Paint Stripping', slug: 'paint-stripping', desc: 'Precision coating removal without damaging underlying surfaces. Ideal for restoration and refinishing.', category: 'specialized' },
  { id: 10, name: 'Rust Removal', slug: 'rust-removal', desc: 'Corrosion treatment and surface preparation for metal components and structures.', category: 'specialized' },
  { id: 11, name: 'HVAC Cleaning', slug: 'hvac-cleaning', desc: 'Thorough cleaning of ducts, coils, and ventilation systems for improved air quality and efficiency.', category: 'facility' },
  { id: 12, name: 'Concrete Cleaning', slug: 'concrete-cleaning', desc: 'Industrial-grade cleaning for floors, walls, and loading docks in commercial and industrial facilities.', category: 'facility' },
  { id: 13, name: 'Facility Maintenance', slug: 'facility-maintenance', desc: 'Beams, ceilings, and structural cleaning for warehouses and manufacturing facilities.', category: 'facility' },
  { id: 14, name: 'Surface Decontamination', slug: 'surface-decontamination', desc: 'Hazmat, chemical, and biological cleanup for controlled environments and sensitive areas.', category: 'facility' },
  { id: 15, name: 'Graffiti Removal', slug: 'graffiti-removal', desc: 'Exterior surface restoration without damaging underlying materials or finishes.', category: 'facility' },
];

const categories = [
  { id: 'all', name: 'All Services' },
  { id: 'core', name: 'Core Services' },
  { id: 'specialized', name: 'Specialized' },
  { id: 'facility', name: 'Facility & Infrastructure' },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter(s => s.category === activeCategory);

  return (
    <main className="bg-black pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6 inline-block">
              15 Service Types
            </span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl uppercase tracking-wider font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Our Services
            </h1>
            <p className="text-zinc-300 text-lg leading-relaxed">
              From core dry ice blasting to specialized restoration and facility maintenance, 
              we offer comprehensive industrial cleaning solutions tailored to your needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-black border-b border-zinc-800 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-2.5 text-sm uppercase tracking-widest font-mono transition-all ${
                  activeCategory === cat.id
                    ? 'bg-orange-600 text-black'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800 border border-zinc-800'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid with Background Images */}
      <section className="py-16 bg-black" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.05 } }
            }}
          >
            {filteredServices.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                data-testid={`service-card-${service.id}`}
              >
                <motion.div
                  variants={fadeInUp}
                  className="group relative h-80 overflow-hidden border border-zinc-800 hover:border-orange-500 transition-all duration-300 cursor-pointer"
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${serviceImages[service.id]})` }}
                  />
                  
                  {/* Dark Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
                  
                  {/* Content */}
                  <div className="relative h-full flex flex-col justify-end p-6 z-10">
                    <div className="flex items-start justify-between mb-3">
                      <span className="badge bg-orange-600 text-black px-3 py-1 text-xs">
                        {service.category}
                      </span>
                      <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
                        {String(service.id).padStart(2, '0')}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl uppercase tracking-wide font-bold text-white mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {service.name}
                    </h3>
                    
                    <p className="text-zinc-300 text-sm leading-relaxed line-clamp-3">
                      {service.desc}
                    </p>
                    
                    <span className="mt-4 text-orange-500 text-sm uppercase tracking-widest font-mono flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>

                  {/* Hover Accent Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Fleet Specific Services */}
      <section className="py-24 bg-zinc-950 border-t border-zinc-800" data-testid="fleet-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6 inline-block">
                Fleet Specialty
              </span>
              <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Fleet-Specific Services
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-8">
                Our dry ice blasting technology is particularly effective for fleet maintenance, 
                offering waterless cleaning that protects sensitive components while delivering 
                exceptional results.
              </p>
              
              <div className="space-y-4">
                {[
                  'Engine Bay Degreasing (no water exposure)',
                  'Frame & Undercarriage Restoration',
                  'Suspension & Drivetrain Cleaning',
                  'Electrical-Safe Cleaning (sensors, wiring harnesses)'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-black border border-zinc-800">
                    <Truck className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1594573677313-757d4aeea449?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                alt="Fleet vehicle engine bay cleaning"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-orange-500/30" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Ready to Experience the Difference?
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Schedule a free on-site demo and see firsthand how our dry ice blasting technology 
            can transform your cleaning operations.
          </p>
          <Link to="/contact" className="btn-primary px-10 py-4 text-lg inline-flex items-center gap-2" data-testid="services-cta">
            Schedule Free Demo
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};
